/**
 * PlaybookOverlay — the "使用手册 · Playbook" surface.
 *
 * Gallery (step 1): reusable question templates grouped by user-facing
 * scenario/capability (用小环 + 上手操作/概念答疑/选型对比/综合排查) — NOT by role;
 * roles are covered *within* each type. Detail (step 2): a CENTERED card (the
 * only difference from Manus, which uses a full page) whose prompt is an inline
 * fill-in — the `{{slots}}` render as highlighted, editable pills inside the
 * sentence. Blank pills fall back to their default (non-blocking). Optional
 * screenshot upload. "开始对话" assembles the prompt (+ images) and reuses App
 * `onSend`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import playbookData from "./playbook.json";
import type { PlaybookTemplate, PlaybookData, PlaybookReplayCase } from "./types";
import { assemblePrompt, groupBySection, gateCases, isNewCard } from "./assemble";
import { LiveRunBlock, useLoomConnected } from "../components/loom/LiveRunBlock";
import { downscaleImage } from "../lib/image";

const DATA = playbookData as unknown as PlaybookData;

interface OutImage {
  data: string;
  mimeType: string;
}
interface PendingImage extends OutImage {
  dataUrl: string;
}

export function hasPlaybook(): boolean {
  return Array.isArray(DATA.cases) && DATA.cases.length > 0;
}

/** Visual width estimate (CJK counts double) so an inline slot auto-sizes. */
function visualLen(s: string): number {
  let n = 0;
  for (const ch of s) n += (ch.codePointAt(0) ?? 0) > 0x2e7f ? 2 : 1;
  return n;
}

const SLOT_RE = /\{\{\s*([\w一-鿿-]+)\s*\}\}/g;

interface PlaybookOverlayProps {
  open: boolean;
  onClose: () => void;
  onLaunch: (prompt: string, images?: OutImage[]) => void;
  /** Trigger a host action (e.g. "feedback" opens the feedback modal). */
  onAction?: (action: string) => void;
  /**
   * Whether the replay feature is on (health `features.replay`, FEATURE_IA_REPLAY).
   * When false, a template's「看真实案例回放」affordance is hidden and the entry
   * behaves exactly as before — the template launch is unchanged. Defaults off.
   */
  replayEnabled?: boolean;
  /**
   * Runtime feature flags (/healthz `features`). A case with a `flag` shows only
   * when that key is true here — generic gating so a release's cases are committed
   * but published by flipping the prod flag. Un-flagged cases always show.
   */
  features?: Record<string, boolean>;
  /**
   * Open the full-screen replay session page for a bound entry's case list. The
   * overlay closes itself first (via `onClose`) so the session page is the only
   * surface on screen — the replay is never cramped inline in the detail modal.
   * The full `replays` list is lifted so the page can render its case switcher.
   */
  onOpenReplay?: (replays: PlaybookReplayCase[], title: string) => void;
}

export function PlaybookOverlay({
  open,
  onClose,
  onLaunch,
  onAction,
  replayEnabled = false,
  features,
  onOpenReplay
}: PlaybookOverlayProps): JSX.Element | null {
  const [selected, setSelected] = useState<PlaybookTemplate | null>(null);
  const [slotValues, setSlotValues] = useState<Record<string, string>>({});
  const [images, setImages] = useState<PendingImage[]>([]);
  // "在你的织灵里试跑" runs the assembled template prompt live, in-overlay (D in
  // the answer-rendering funnel: 用我的环境试). Only offered when connected.
  const [runPrompt, setRunPrompt] = useState<string | null>(null);
  const connected = useLoomConnected();
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Hide cases gated on a runtime flag that is off — committed but unpublished
  // until the prod flag flips. Generic: any case `flag` keyed into `features`.
  const groups = useMemo(() => groupBySection({ ...DATA, cases: gateCases(DATA.cases, features) }), [features]);

  const reset = (): void => {
    setSelected(null);
    setSlotValues({});
    setImages([]);
    setRunPrompt(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (selected) reset();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selected, onClose]);

  if (!open) return null;

  const openCase = (c: PlaybookTemplate): void => {
    setSelected(c);
    setSlotValues({});
    setImages([]);
  };

  const onPickFiles = (files: FileList | null): void => {
    if (!files) return;
    Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, 3)
      .forEach((file) => {
        // downscale before upload so the vision model isn't fed a multi-MB
        // screenshot (which timed out the LLM call)
        void downscaleImage(file).then((enc) => {
          if (enc) setImages((prev) => [...prev, enc]);
        });
      });
  };

  /** Send a finished prompt (+ any attached images) and close the overlay. */
  const launchPrompt = (prompt: string): void => {
    if (!prompt) return;
    const out = images.map((i) => ({ data: i.data, mimeType: i.mimeType }));
    onClose();
    onLaunch(prompt, out.length > 0 ? out : undefined);
  };

  const launch = (): void => {
    if (!selected) return;
    launchPrompt(assemblePrompt(selected, slotValues));
  };

  /** Render the template sentence with inline editable slot pills (Manus-style). */
  const renderFill = (t: PlaybookTemplate): JSX.Element[] => {
    const byKey = new Map((t.slots ?? []).map((s) => [s.key, s]));
    // split on the capturing slot regex → even parts = literal text, odd = slot keys
    const parts = t.promptTemplate.split(SLOT_RE);
    return parts.map((part, idx) => {
      if (idx % 2 === 0) return <span key={idx}>{part}</span>;
      const key = part;
      const slot = byKey.get(key);
      const ghost = slot?.default ?? slot?.placeholder ?? key;
      const val = slotValues[key] ?? "";
      const shown = val.length > 0 ? val : ghost;
      const w = Math.max(visualLen(shown), 2) * 0.52 + 1.5;
      // UNCONTROLLED input (defaultValue + onInput) so a Chinese IME composition
      // is never interrupted by a re-render forcing `value` back — a controlled
      // value breaks IME typing. Keyed by template+idx so switching templates
      // remounts clean; state still tracks the value for width + launch.
      return (
        <input
          key={`${t.id}-${idx}`}
          className="pb-fill-slot"
          type="text"
          defaultValue={val}
          placeholder={ghost}
          style={{ width: `${w}em` }}
          aria-label={slot?.label ?? key}
          onInput={(e) => {
            // capture the value synchronously — `currentTarget` is null inside the
            // async setState updater (the synthetic event is reset after dispatch)
            const v = (e.target as HTMLInputElement).value;
            setSlotValues((p) => ({ ...p, [key]: v }));
          }}
        />
      );
    });
  };

  return (
    <div className="pb-overlay" role="dialog" aria-modal="true" aria-label="小环使用手册">
      <header className="pb-top">
        <div>
          <h2 className="pb-title">看看可以这样问小环</h2>
          <p className="pb-subtitle">挑一个模板，填上你的情况就能问；不填也能直接开始</p>
        </div>
        <button className="pb-close" onClick={onClose} aria-label="关闭">
          ✕
        </button>
      </header>

      <div className="pb-scroll">
        {groups.map((g) => (
          <section className="pb-cat" key={g.section}>
            <div className="pb-cat-head">
              <span className="pb-cat-label">{g.section}</span>
            </div>
            <div className="pb-cards">
              {g.cases.map((c) => (
                <button
                  className="pb-card"
                  key={c.id}
                  onClick={() => (c.action ? onAction?.(c.action) : openCase(c))}
                >
                  {isNewCard(c.since) ? <span className="pb-new">NEW</span> : null}
                  <span className="pb-thumb">
                    <img
                      className="pb-thumb-img pb-thumb-light"
                      src={`/${c.thumb.light}`}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      className="pb-thumb-img pb-thumb-dark"
                      src={`/${c.thumb.dark}`}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                  <span className="pb-card-title">{c.title}</span>
                  {c.intent ? <span className="pb-card-intent">{c.intent}</span> : null}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {selected ? (
        <>
          <div className="pb-scrim" onClick={reset} aria-hidden="true" />
          <div className="pb-modal" role="document">
            <button className="pb-modal-close" onClick={reset} aria-label="返回">
              ✕
            </button>
            <span className="pb-modal-thumb">
              <img className="pb-thumb-img pb-thumb-light" src={`/${selected.thumb.light}`} alt="" />
              <img className="pb-thumb-img pb-thumb-dark" src={`/${selected.thumb.dark}`} alt="" />
            </span>
            <h3 className="pb-modal-title">{selected.title}</h3>
            {selected.intent ? <p className="pb-modal-intent">{selected.intent}</p> : null}

            <div className="pb-prompt-label">把高亮处换成你的情况，或直接开始</div>
            <div className="pb-fill">{renderFill(selected)}</div>
            <p className="pb-note">高亮处可直接填；留空就用默认值，不影响开始提问。</p>

            {selected.examples && selected.examples.length > 0 ? (
              <div className="pb-examples">
                <span className="pb-examples-label">也可以直接问这些</span>
                <div className="pb-example-chips">
                  {selected.examples.slice(0, 3).map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      className="pb-example-chip"
                      onClick={() => launchPrompt(ex)}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {selected.needsAttachment ? (
              <div className="pb-upload">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => onPickFiles(e.target.files)}
                />
                <button type="button" className="pb-upload-btn" onClick={() => fileRef.current?.click()}>
                  + 添加截图（可选）
                </button>
                {images.length > 0 ? (
                  <div className="pb-upload-thumbs">
                    {images.map((im, i) => (
                      <span className="pb-upload-thumb" key={im.dataUrl}>
                        <img src={im.dataUrl} alt="" />
                        <button
                          type="button"
                          aria-label="移除"
                          onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* 在你的织灵里试跑: run THIS template's assembled prompt live, in the
                overlay (funnel D: 用我的环境试). Compact + top-level here, so it is
                clearly visible — unlike the replay-bar run buried under a long
                recording. Gated on loom-live (FEATURE_IA_LOOM_LIVE) AND a connected
                织灵 — both required to actually run, so neither alone offers it. */}
            {connected === true && features?.loomLive === true ? (
              runPrompt !== null ? (
                <div className="pb-tryrun">
                  <LiveRunBlock prompt={runPrompt} />
                </div>
              ) : (
                <button
                  type="button"
                  className="pb-tryrun-btn"
                  onClick={() => setRunPrompt(assemblePrompt(selected, slotValues))}
                >
                  在你的织灵里试跑 →
                </button>
              )
            ) : null}

            {/* Dual-action (Manus-style) when a worked-example replay is bound and
                the feature is on: 看真实案例回放 (primary — opens the FULL-SCREEN
                session page, closing this overlay) + 自己问一遍 (launch a chat).
                Otherwise the single 开始对话 launch — the entry is unchanged. */}
            {selected.replays && selected.replays.length > 0 && replayEnabled ? (
              <div className="pb-modal-actions">
                <button
                  className="pb-btn-primary"
                  onClick={() => {
                    const r = selected.replays;
                    if (r === undefined || r.length === 0) return;
                    onClose();
                    onOpenReplay?.(r, selected.title);
                  }}
                >
                  看真实案例回放
                </button>
                <button className="pb-btn-ghost" onClick={launch}>
                  自己问一遍 · 开始对话
                </button>
              </div>
            ) : (
              <div className="pb-modal-actions">
                <button className="pb-btn-primary" onClick={launch}>
                  {selected.needsAttachment && images.length > 0 ? "带截图开始" : "开始对话"}
                </button>
                <button className="pb-btn-ghost" onClick={reset}>
                  返回
                </button>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
